// Přesnost Prediction Modelu v2 — porovnání zmrazených predikcí se skutečnými body.
import React, { useEffect, useState } from "react";
import { Target, CaretDown, CaretUp } from "@phosphor-icons/react";
import { getPredictionAccuracy } from "../lib/api";

function Metric({ label, value, suffix = "" }) {
    return (
        <div className="border border-white/10 px-3 py-2">
            <div className="font-mono text-[8px] uppercase tracking-widest text-white/40">{label}</div>
            <div className="font-display font-black text-lg text-fa-mint tabular-nums">{value}{suffix}</div>
        </div>
    );
}

export const ModelAccuracy = () => {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => { getPredictionAccuracy().then(setData).catch(() => {}); }, []);
    if (!data) return null;

    return (
        <div className="border border-white/10 bg-[#0d0d0d]" data-testid="model-accuracy">
            <button type="button" onClick={() => setOpen(!open)} data-testid="model-accuracy-toggle"
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02]">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/70 flex items-center gap-1.5">
                    <Target size={12} className="text-fa-yellow" /> Přesnost modelu
                    {data.overall
                        ? <span className="text-fa-mint ml-2">MAE {data.overall.mae} b · pokrytí intervalu {data.overall.coverage_pct} % · {data.overall.gws} kol</span>
                        : <span className="text-white/40 ml-2">zatím žádné vyhodnocené kolo</span>}
                </span>
                {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-4">
                    {data.snapshot_pending && (
                        <p className="font-mono text-[10px] text-fa-sky" data-testid="accuracy-pending">
                            Predikce pro GW {data.snapshot_pending.gw} zmrazeny ({data.snapshot_pending.players} hráčů) — vyhodnotí se automaticky po odehrání kola.
                        </p>
                    )}
                    {data.overall && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <Metric label="MAE (Ø chyba)" value={data.overall.mae} suffix=" b" />
                            <Metric label="Bias (přestřeluje +)" value={data.overall.bias} suffix=" b" />
                            <Metric label="Skutečnost ve floor–strop" value={data.overall.coverage_pct} suffix=" %" />
                            <Metric label="Top 10 shoda / kolo" value={data.overall.top10_overlap_avg} suffix=" /10" />
                        </div>
                    )}
                    {data.gws.length > 0 && (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="font-mono text-[8px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                    <th className="py-1.5 pr-3">GW</th><th className="py-1.5 pr-3">Hráčů</th>
                                    <th className="py-1.5 pr-3">MAE</th><th className="py-1.5 pr-3">RMSE</th>
                                    <th className="py-1.5 pr-3">Bias</th><th className="py-1.5 pr-3">Pokrytí</th>
                                    <th className="py-1.5">Top10 shoda</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.gws.map((g) => (
                                    <tr key={g.gw} className="border-b border-white/5 font-mono text-[11px] text-white/70 tabular-nums" data-testid={`accuracy-gw-${g.gw}`}>
                                        <td className="py-1.5 pr-3 font-bold text-white/90">GW {g.gw}</td>
                                        <td className="py-1.5 pr-3">{g.players}</td>
                                        <td className="py-1.5 pr-3 text-fa-mint">{g.mae}</td>
                                        <td className="py-1.5 pr-3">{g.rmse}</td>
                                        <td className="py-1.5 pr-3">{g.bias > 0 ? `+${g.bias}` : g.bias}</td>
                                        <td className="py-1.5 pr-3">{g.coverage_pct} %</td>
                                        <td className="py-1.5">{g.top10_overlap}/10</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {(data.last_hits.length > 0 || data.last_misses.length > 0) && (
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <p className="font-mono text-[8px] uppercase tracking-widest text-fa-mint mb-1">Nejpřesnější trefy (poslední kolo)</p>
                                {data.last_hits.map((s) => (
                                    <p key={s.fpl_id} className="font-mono text-[10px] text-white/65">{s.name}: pred {s.predicted} → {s.actual} b</p>
                                ))}
                            </div>
                            <div>
                                <p className="font-mono text-[8px] uppercase tracking-widest text-[#FF8800] mb-1">Největší minely</p>
                                {data.last_misses.map((s) => (
                                    <p key={s.fpl_id} className="font-mono text-[10px] text-white/65">{s.name}: pred {s.predicted} → {s.actual} b ({s.error > 0 ? "+" : ""}{s.error})</p>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="font-mono text-[8px] text-white/35">
                        Před každým kolem se predikce zmrazí; po odehrání se porovnají se skutečnými body. MAE = průměrná absolutní chyba, bias &gt; 0 = model podstřeluje.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ModelAccuracy;
